import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { AuthenticationService, AppConfigService } from '@alfresco/adf-core';

/** Full license status returned by GET /bpm/api/menu/license-status. */
export interface LicenseStatus {
  client: string;
  edition: string | null;
  valid: boolean;
  modules: string[];
  perpetual: boolean;
  expiryDate: string | null;
  daysUntilExpiry: number;
  expiryWarningLevel: string;
  seatsTotal: number;
  seatsUnlimited: boolean;
  seatsInUse: number;
  fingerprintOk: boolean;
  fingerprintWarning: boolean;
  binding: string;
  serverHostname: string;
  serverMac: string;
}

export interface InstallResult {
  success: boolean;
  message: string;
  [key: string]: any;
}

/**
 * Talks to the backend license API (under /bpm/api/menu):
 *   GET  /license-status   → full status for the License page
 *   POST /license/install  → activate a pasted token (SKYPLUS_ADMIN only, gated server-side)
 */
@Injectable({ providedIn: 'root' })
export class SkyplusLicenseService {
  private readonly base: string;

  constructor(
    private http: HttpClient,
    private auth: AuthenticationService,
    appConfig: AppConfigService
  ) {
    const apiBase = appConfig.get<string>('skyplus.apiUrl', '/bpm/api');
    this.base = `${apiBase}/menu`;
  }

  getStatus(): Observable<LicenseStatus> {
    return this.http.get<LicenseStatus>(`${this.base}/license-status`, { headers: this.authHeaders() });
  }

  install(token: string): Observable<InstallResult> {
    return this.http.post<InstallResult>(
      `${this.base}/license/install`,
      { token },
      { headers: this.authHeaders('application/json') }
    );
  }

  private authHeaders(contentType?: string): HttpHeaders {
    let h = new HttpHeaders();
    try {
      const t = this.auth.getToken();
      if (t) { h = h.set('Authorization', `Bearer ${t}`); }
    } catch { /* no token */ }
    if (contentType) { h = h.set('Content-Type', contentType); }
    return h;
  }
}
